import { useEffect, useRef } from 'react';
import { SERVICE_AREAS, BUSINESS_INFO } from '../../mocks/lisaMerkleData';
import { Link } from 'react-router-dom';

const AREA_DESCRIPTIONS: Record<string, string> = {
  'Beverly Hills': 'Premier luxury estates and custom residences',
  'Los Angeles': 'Citywide residential and commercial projects',
  'West Hollywood': 'Design-forward homes in a vibrant market',
  'Pacific Palisades': 'Elevated homes with coastal-canyon character',
  'Brentwood': 'Sophisticated family estates and private residences',
  'Century City': 'High-rise condominiums and executive spaces',
  'Malibu': 'Oceanfront retreats and coastal luxury homes',
  'West Los Angeles': 'Contemporary residences and creative spaces',
  'Hollywood': 'Historic and architecturally distinct properties',
  'Hollywood Hills': 'Iconic hillside estates with city views',
  'Bel Air': 'Ultra-luxury gated community estates',
  'Santa Monica': 'Coastal California homes and creative residences',
  'Westwood': 'Classic residential neighborhoods near UCLA',
  'Hancock Park': 'Historic estates with architectural distinction',
  'Encino': 'Family estates in the prestigious San Fernando Valley',
  'Beverly Park': 'Ultra-private gated luxury community',
  'Hidden Hills': 'Equestrian estates and rural luxury properties',
};

export default function ServiceAreasSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.querySelectorAll('.reveal').forEach((node, i) => {
          setTimeout(() => node.classList.add('revealed'), i * 60);
        });
        obs.disconnect();
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-charcoal py-28 overflow-hidden relative">
      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-5"
        style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(196,180,154,0.3) 0%, transparent 60%)' }}
      />

      <div className="max-w-[1400px] mx-auto px-8 relative z-10">
        <div className="reveal grid md:grid-cols-2 gap-12 items-end mb-16">
          <div>
            <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">Where We Work</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-cream mt-3 leading-tight">
              Service Areas
            </h2>
            <p className="text-cream/45 text-sm leading-relaxed font-sans mt-5 max-w-md">
              From Beverly Hills estates to Malibu retreats — serving Los Angeles's most prestigious communities and discerning clients nationwide.
            </p>
          </div>
          <div className="flex flex-col items-start md:items-end justify-end gap-4">
            <a
              href={BUSINESS_INFO.phoneHref}
              className="inline-flex items-center gap-3 px-8 py-4 border border-stone/30 text-cream text-[10px] tracking-[0.2em] uppercase font-sans rounded-full hover:bg-stone/10 hover:border-stone/50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-stone text-sm" />
              {BUSINESS_INFO.phone}
            </a>
            <p className="text-cream/25 text-[10px] font-sans tracking-wider">Also serving clients nationwide · US-wide project leadership</p>
          </div>
        </div>

        {/* Areas Grid with descriptions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-cream/5">
          {SERVICE_AREAS.map((area, i) => (
            <Link
              key={area}
              to="/location"
              className={`reveal bg-charcoal-soft/0 hover:bg-charcoal-soft/60 p-6 transition-all duration-400 group cursor-pointer border-b border-cream/5 ${i < 3 ? `reveal-delay-${i + 1}` : ''}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-stone/60 group-hover:bg-stone flex-shrink-0 transition-colors" />
                    <h3 className="text-cream/85 font-sans font-medium text-sm group-hover:text-cream transition-colors">{area}</h3>
                  </div>
                  <p className="text-cream/30 text-[11px] leading-snug font-sans pl-3.5">
                    {AREA_DESCRIPTIONS[area] || 'Luxury residential and commercial design'}
                  </p>
                </div>
                <i className="ri-arrow-right-up-line text-stone/30 group-hover:text-stone transition-colors text-xs flex-shrink-0 mt-0.5" />
              </div>
            </Link>
          ))}
        </div>

        <div className="reveal mt-10 pt-8 border-t border-cream/8 text-center">
          <p className="text-cream/30 text-xs font-sans tracking-wider">
            <strong className="text-stone font-normal">Beverly Hills Studio</strong> · 215 N La Peer Dr, Beverly Hills, CA 90211 ·
            <a href={BUSINESS_INFO.phoneHref} className="text-stone hover:text-stone-light transition-colors cursor-pointer"> {BUSINESS_INFO.phone}</a>
          </p>
        </div>
      </div>
    </section>
  );
}
